import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trpc } from "@/trpc/trpc";
import { toast } from "sonner";

const schema = z.object({
  name: z.string().min(1, "Nazwa jest wymagana"),
  code: z.string().min(1, "Kod jest wymagany").max(20),
  email: z.string().email("Nieprawidłowy email").or(z.literal("")).optional(),
  status: z.enum(["active", "inactive", "suspended"]),
  licenseExpiresAt: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

export type SalonItem = {
  id: string;
  code: string;
  name: string;
  email: string | null;
  status: "active" | "inactive" | "suspended";
  adminOrgId: string | null;
  licenseExpiresAt: string | null;
  createdAt: string;
};

export function SalonEditDialog({
  open,
  onOpenChange,
  salon,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  salon?: SalonItem | null;
}) {
  const utils = trpc.useUtils();
  const isEdit = !!salon;

  const createMutation = trpc.admin.createSalon.useMutation({
    onSuccess: () => {
      utils.admin.listSalons.invalidate();
      utils.admin.dashboardStats.invalidate();
      toast.success("Salon utworzony");
      onOpenChange(false);
    },
    onError: (err) => toast.error(err.message),
  });

  const updateMutation = trpc.admin.updateSalon.useMutation({
    onSuccess: () => {
      utils.admin.listSalons.invalidate();
      toast.success("Salon zaktualizowany");
      onOpenChange(false);
    },
    onError: (err) => toast.error(err.message),
  });

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      code: "",
      email: "",
      status: "active",
      licenseExpiresAt: "",
    },
  });

  useEffect(() => {
    if (open) {
      if (salon) {
        form.reset({
          name: salon.name,
          code: salon.code,
          email: salon.email ?? "",
          status: salon.status,
          licenseExpiresAt: salon.licenseExpiresAt
            ? salon.licenseExpiresAt.split("T")[0]
            : "",
        });
      } else {
        form.reset({
          name: "",
          code: "",
          email: "",
          status: "active",
          licenseExpiresAt: "",
        });
      }
    }
  }, [open, salon, form]);

  const onSubmit = form.handleSubmit((data) => {
    const licenseExpiresAt = data.licenseExpiresAt
      ? new Date(data.licenseExpiresAt + "T23:59:59Z").toISOString()
      : null;

    if (isEdit && salon) {
      updateMutation.mutate({
        salonId: salon.id,
        name: data.name,
        email: data.email || null,
        status: data.status,
        licenseExpiresAt,
      });
    } else {
      createMutation.mutate({
        name: data.name,
        code: data.code,
        email: data.email || null,
      });
    }
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edytuj salon" : "Nowy salon"}
          </DialogTitle>
        </DialogHeader>
        <DialogForm onSubmit={onSubmit}>
          <DialogBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="s-name">Nazwa</Label>
              <Input id="s-name" {...form.register("name")} />
              {form.formState.errors.name && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.name.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="s-code">Kod</Label>
              <Input
                id="s-code"
                className="font-mono uppercase"
                disabled={isEdit}
                {...form.register("code")}
              />
              {form.formState.errors.code && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.code.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="s-email">Email kontaktowy</Label>
              <Input id="s-email" type="email" {...form.register("email")} />
            </div>

            {isEdit && (
              <>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Controller
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Aktywny</SelectItem>
                          <SelectItem value="inactive">Nieaktywny</SelectItem>
                          <SelectItem value="suspended">Zawieszony</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="s-license">Licencja ważna do</Label>
                  <Input
                    id="s-license"
                    type="date"
                    {...form.register("licenseExpiresAt")}
                  />
                </div>
              </>
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Anuluj
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending
                ? "Zapisywanie..."
                : isEdit
                  ? "Zapisz zmiany"
                  : "Utwórz salon"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
